import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getReport, getAssets, getReportPdfUrl } from '../services/api';
import { ensureAccessToken } from '../services/session';
import { Button } from '../components/ui/button';
import { CheckCircle2, FileText, Lock, Download, ArrowLeft } from 'lucide-react';
import LutromLogo from '../components/LutromLogo';
import PoweredByLutrom from '../components/PoweredByLutrom';

const ReportView = () => {
  const { reportId } = useParams();
  const { user, loading } = useAuth();
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [asset, setAsset] = useState(null);

  useEffect(() => {
    if (!user) return;
    getReport(reportId)
      .then(async (r) => {
        setReport(r);
        const assets = await getAssets().catch(() => []);
        setAsset(assets.find(a => a.id === r.assetId));
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Report not found'));
  }, [reportId, user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!user) return <Navigate to={`/login?redirect=/report/${reportId}`} replace />;

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center">
        <Lock className="h-10 w-10 text-blue-700 mx-auto mb-3" />
        <h1 className="font-display uppercase text-xl text-slate-900 mb-2">{error}</h1>
        <Link to="/dashboard"><Button variant="outline">Back</Button></Link>
      </div>
    </div>
  );

  if (!report) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading report...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto p-6 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-300 hover:text-white text-sm"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
          <LutromLogo dark size="sm" />
        </div>
      </header>
      <main className="max-w-3xl mx-auto p-6">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="bg-slate-900 text-white p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-xs font-mono text-blue-400 uppercase tracking-widest">{report.id}</div>
              <Button size="sm" variant="outline" className="border-slate-600 text-white bg-transparent hover:bg-white/10" onClick={() => {
                ensureAccessToken()
                  .then(token => fetch(getReportPdfUrl(report.id), { headers: { Authorization: `Bearer ${token}` } }))
                  .then(r => r.blob())
                  .then(b => {
                    const url = URL.createObjectURL(b);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `lutrom-${report.id}.pdf`;
                    a.click();
                    URL.revokeObjectURL(url);
                  });
              }}><Download className="h-4 w-4 mr-1" /> PDF</Button>
            </div>
            <h1 className="font-display uppercase text-3xl mb-2">{report.type}</h1>
            <div className="text-sm text-slate-300">Issued {report.issuedAt} · by {report.engineer}</div>
          </div>
          <div className="p-6 space-y-6">
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div><div className="text-xs uppercase tracking-wider text-slate-500">Site</div><div className="font-medium">{asset?.location || report.siteId}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-slate-500">Asset</div><div className="font-medium">{asset?.name || '—'}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-slate-500">Work Order</div><div className="font-mono">{report.workOrderId}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-slate-500">Engineer</div><div className="font-medium">{report.engineer}</div></div>
            </div>
            <div><div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Summary</div><p className="text-slate-800 leading-relaxed">{report.summary}</p></div>
            <div><div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Findings</div>
              <ul className="space-y-2">
                {report.findings.map((f, i) => <li key={i} className="flex items-start gap-2 text-slate-800"><CheckCircle2 className="h-4 w-4 text-blue-800 mt-0.5 shrink-0" /> {f}</li>)}
              </ul>
            </div>
            <div className="pt-6 border-t border-slate-200 flex items-center gap-2 text-sm text-slate-500"><FileText className="h-4 w-4" /> Verified Lutrom Engineering Report</div>
          </div>
        </div>
        <PoweredByLutrom />
      </main>
    </div>
  );
};

export default ReportView;
